import {
  leadsTasksListQueryKey,
  opportunitiesTasksListQueryKey,
  ReturnTaskReadable,
  tasksDestroyMutation,
  tasksListOptions,
  tasksListQueryKey,
  tasksPartialUpdateMutation
} from '@crm/api'
import { ConfirmModal, TaskForm } from '@crm/components'
import { OpportunityContextProvider } from '@crm/contexts/OpportunityContext'
import { TaskFilter } from '@crm/features/tasks/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { paths } from '@crm/routes'
import { getActivePath, isFeature, labelStatus, labelText } from '@crm/utils'
import {
  ConditionalText,
  DayLabel,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryUpdate,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getContrastTextColor, getFullName, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Flex, Modal, notification, Segmented, Tag } from 'antd'
import { Edit2, Kanban, TextalignJustifycenter, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import { useLocation, useParams } from 'react-router-dom'

import type { MenuProps, SegmentedProps, TableColumnsType } from 'antd'

const formName = 'task-form'

export const Tasks = () => {
  const { t } = useLocales()
  const { params, updateQuery } = useQueryUpdate()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const sorter = useTableSorter()
  const navigate = useAppNavigate()
  const { id } = useParams()
  const { pathname } = useLocation()
  const { resetPagination } = useResetPagination()

  const [loadingTaskId, setLoadingTaskId] = useState<number>()

  const [modalState, setModalState] = useState<{
    isOpen?: boolean
    task: ReturnTaskReadable | null
    type: 'delete' | 'edit' | 'add' | null
  }>({ task: null, type: null })

  const { data: tasks, isLoading } = useQuery(
    tasksListOptions({
      query: {
        ...params,
        completed:
          params.completed === 'all' ? undefined : params.completed || 0,
        deadline_start: params.deadline_start,
        deadline_end: params.deadline_end
      }
    })
  )

  const { mutate: deleteMutate } = useMutation({
    ...tasksDestroyMutation(),
    onSuccess: () => {
      onSuccessMutate()
      resetPagination()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const { mutate } = useMutation({
    ...tasksPartialUpdateMutation(),
    onSuccess: (data) => {
      setLoadingTaskId(undefined)
      onSuccessMutate(data)
    },
    onError: (e) => showApiErrors(e)
  })

  const onRow = (data: ReturnTaskReadable) => ({
    onClick: () => navigate(`${paths.leads}/${data?.lead?.id}/tasks`)
  })

  const onSuccessMutate = (data?: ReturnTaskReadable) => {
    invalidateQueries([
      tasksListQueryKey(),

      data?.lead?.id &&
        leadsTasksListQueryKey({
          path: {
            lead_id: String(data?.lead?.id)
          }
        }),

      data?.opportunity?.id &&
        opportunitiesTasksListQueryKey({
          path: {
            opportunity_id: String(data?.opportunity?.id)
          }
        })
    ])
    onCancel()
  }

  const onCell = () => ({
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.MouseEvent) => e.stopPropagation()
  })

  const onEdit = (task: ReturnTaskReadable) => {
    setModalState({
      type: 'edit',
      task
    })
  }

  const onCancel = () => {
    setModalState({ task: null, isOpen: false, type: null })
  }

  const onDelete = (task: ReturnTaskReadable) => {
    deleteMutate({ path: { id: task?.id } })
  }

  const onCompleteTask = useCallback(
    (task: ReturnTaskReadable) => {
      setLoadingTaskId(task.id)
      mutate({
        path: { id: task?.id },
        body: { completed_at: new Date().toISOString() }
      })
    },
    [mutate]
  )

  const getActionColumnItem: (task: ReturnTaskReadable) => MenuProps['items'] =
    useMemo(
      () => (task) =>
        [
          {
            label: t('documents.edit'),
            key: 'edit',
            icon: <Edit2 size={20} />,
            onClick: () => onEdit(task)
          },
          { type: 'divider' },
          {
            label: t('documents.delete'),
            key: 'delete',
            danger: true,
            icon: <Trash size={20} />,
            onClick: () =>
              setModalState({
                type: 'delete',
                task
              })
          }
        ],
      [t]
    )

  const getTextType = (completedAt: unknown, deadline: string | Date) => {
    if (completedAt) return undefined
    return isFeature(deadline) ? 'warning' : 'danger'
  }

  const columns: TableColumnsType<ReturnTaskReadable> = useMemo(
    () => [
      {
        title: t('task.status'),
        onCell,
        render: (task) => (
          <Flex justify="center">
            <Button
              onClick={() => !task?.completed_at && onCompleteTask(task)}
              loading={loadingTaskId === task.id}
              disabled={task?.completed_at}
              size="small"
            >
              {t(
                task?.completed_at
                  ? 'task.completed_task'
                  : 'task.complete_task'
              )}
            </Button>
          </Flex>
        )
      },
      {
        title: t('task.title'),
        key: 'title',
        sorter: true,
        render: (_, { title }) => (
          <ConditionalText
            className="table-column-max-width"
            isEllipsis
            condition={!!title}
          >
            {title}
          </ConditionalText>
        )
      },
      {
        title: t('task.status'),
        key: 'status',
        sorter: true,
        width: 180,
        render: (_, { status }) =>
          status?.name ? (
            <Tag
              color={status?.color ?? 'default'}
              style={{ color: getContrastTextColor(status?.color) }}
            >
              {status?.name}
            </Tag>
          ) : (
            '-'
          )
      },
      {
        title: t('task.short_description'),
        render: ({ description }) => (
          <ConditionalText
            condition={description}
            className="table-column-max-width"
            isEllipsis
          >
            {description}
          </ConditionalText>
        )
      },
      {
        title: t('task.date_time'),
        key: 'deadline',
        sorter: true,
        render: ({ completed_at, deadline }) => (
          <DayLabel
            date={deadline}
            textProps={{
              type: getTextType(completed_at, deadline),
              delete: completed_at
            }}
          />
        )
      },
      {
        title: t('task.priority'),
        key: 'priority',
        sorter: true,
        render: ({ completed_at, priority }) =>
          priority ? (
            <Flex justify="center">
              <Tag
                color={
                  completed_at
                    ? 'default'
                    : labelStatus[priority as keyof typeof labelStatus]
                }
              >
                {t(labelText[priority as keyof typeof labelText])}
              </Tag>
            </Flex>
          ) : (
            '-'
          )
      },
      {
        title: t('task.responsible_person'),
        key: 'owner',
        sorter: true,
        render: ({ completed_at, owner }) => {
          const fullName = getFullName(owner)

          return (
            <ConditionalText
              condition={!!fullName}
              type={completed_at ? 'secondary' : undefined}
              delete={completed_at}
            >
              {fullName}
            </ConditionalText>
          )
        }
      },
      {
        title: t('task.lead_name'),
        key: 'name',
        sorter: true,
        onCell,
        render: ({ completed_at, lead }) => (
          <ConditionalText
            condition={lead?.name}
            type={completed_at && 'secondary'}
            delete={completed_at}
          >
            {lead?.name}
          </ConditionalText>
        )
      },
      {
        title: t('contracts.opportunity'),
        key: 'opportunity',
        dataIndex: 'opportunity',
        sorter: true,
        render: (opportunity) => (
          <ConditionalText condition={opportunity?.value}>
            {`${opportunity?.value} ${
              opportunity?.currency ? opportunity?.currency : ''
            }`}
          </ConditionalText>
        )
      },
      {
        title: t('filter.type'),
        key: 'type',
        dataIndex: 'type',
        sorter: true,
        render: (type) => (
          <ConditionalText condition={type?.name}>{type?.name}</ConditionalText>
        )
      },
      {
        title: t('task.task_author'),
        key: 'created_by',
        sorter: true,
        render: ({ created_by, completed_at }) => (
          <ConditionalText
            condition={created_by?.name}
            type={completed_at && 'secondary'}
            delete={completed_at}
          >
            {created_by?.name}
          </ConditionalText>
        )
      },
      {
        width: 50,
        fixed: 'right',
        key: 'actions',
        render: (task) =>
          !task.completed_at ? (
            <TableActions options={getActionColumnItem(task)} />
          ) : (
            '-'
          )
      }
    ],
    [t, loadingTaskId, onCompleteTask, getActionColumnItem]
  )

  const statusOptions: SegmentedProps<string>['options'] = [
    {
      label: t('filter.uncompleted'),
      value: '0'
    },
    { label: t('filter.completed'), value: '1' },
    { label: t('filter.all'), value: 'all' }
  ]

  const segmentedOptions: SegmentedProps<string>['options'] = [
    { value: paths.tasks.root, label: <TextalignJustifycenter size={16} /> },
    { value: paths.tasks.kanban, label: <Kanban size={16} /> }
  ]

  const isEdit = modalState.type === 'edit'

  return (
    <OpportunityContextProvider id={id}>
      <TitleSection
        title={t(`menu.tasks`)}
        count={tasks?.count}
        isLoading={isLoading}
      />

      <TableContainer
        rowKey="id"
        data={tasks}
        onRow={onRow}
        isHoverAlternative
        showSearch
        showSorterTooltip={false}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        leftChildren={<TaskFilter />}
        rightChildren={
          <Flex gap={8}>
            <Segmented
              options={statusOptions}
              value={params.completed}
              onChange={(value) => updateQuery({ completed: value })}
            />

            <Segmented
              options={segmentedOptions}
              value={getActivePath(pathname)}
              onChange={(value) => navigate(value)}
            />
          </Flex>
        }
      />

      {(isEdit || modalState.type === 'add') && (
        <Modal
          width={800}
          open={modalState.isOpen || !!modalState.task}
          maskClosable={false}
          title={t(isEdit ? 'task.edit_task' : 'task.add_task')}
          onCancel={onCancel}
          destroyOnHidden
          cancelText={t('buttons.cancel')}
          okButtonProps={{
            htmlType: 'submit',
            form: formName
          }}
          okText={t('buttons.save')}
        >
          <TaskForm
            formName={formName}
            id={Number(modalState?.task?.id)}
            onCancel={onCancel}
          />
        </Modal>
      )}

      <ConfirmModal
        open={modalState.type === 'delete'}
        onSuccess={() => onDelete(modalState?.task as ReturnTaskReadable)}
        onCancel={onCancel}
      />
    </OpportunityContextProvider>
  )
}
